import './styles.scss';

import React, { useMemo } from 'react';

import {
  Button,
  message,
  Typography,
} from 'antd';
import moment from 'moment';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { MdDownload } from 'react-icons/md';

import API from '../../../../api';
import showAppError from '../../../../shared/error';
import { useAppSelector } from '../../../../shared/hooks';
import { newNumberFormat } from '../../../../shared/utils';
import {
  IOrder,
  ISubscription,
} from '../../../../types/userTypes';

interface Props {
  subscription: ISubscription;
}

const getInstallmentNumber = (inst: {
  installmentNumber?: number;
  installmentIndex?: number;
}) => inst?.installmentNumber ?? inst?.installmentIndex ?? null;

const sortByInstallment = <
  T extends { installmentNumber?: number; installmentIndex?: number },
>(
  list: T[] | undefined,
) =>
  [...(list || [])].sort((a, b) => {
    const na = getInstallmentNumber(a);
    const nb = getInstallmentNumber(b);
    if (na != null && nb != null) return na - nb;
    if (na != null) return -1;
    if (nb != null) return 1;
    return 0;
  });

const isPaid = (inst: { paidOn?: string }) => Boolean(inst?.paidOn);

const getAmountForCurrency = (
  inst: { amount?: Partial<Record<string, number>> },
  currencyKey: string,
) => {
  const amt = inst?.amount;
  if (!amt || typeof amt !== 'object') return null;
  const key = (currencyKey || 'inr').toLowerCase();
  if (amt[key as keyof typeof amt] != null) {
    return amt[key as keyof typeof amt] as number;
  }
  const upper = key.toUpperCase() as keyof typeof amt;
  if (amt[upper] != null) return amt[upper] as number;
  return amt.inr ?? amt.usd ?? amt.eur ?? amt.INR ?? amt.USD ?? amt.EUR ?? null;
};

type InstallmentRow = {
  key: string;
  installmentNo: number | null;
  displayIndex: number;
  amountVal: number | null;
  dueLabel: string;
  status: 'PAID' | 'DUE' | 'UPCOMING';
  isDue: boolean;
  paid: boolean;
  invoiceUrl?: string;
};

const findOrderForInstallment = (
  orders: IOrder[] | undefined,
  installmentNumber: number | null,
  displayIndexOneBased: number,
): IOrder | undefined => {
  if (!orders?.length) return undefined;
  const key =
    installmentNumber != null && !Number.isNaN(Number(installmentNumber))
      ? Number(installmentNumber)
      : displayIndexOneBased;
  return orders.find(
    (o) =>
      o.flexiPayInstallmentNumber != null &&
      Number(o.flexiPayInstallmentNumber) === key,
  );
};

const currencySymbolNode = (currency: string) => {
  if (currency === 'EUR') {
    return <span className="inr">&#x20AC;</span>;
  }
  if (currency === 'USD') {
    return <span className="inr">&#x24;</span>;
  }
  return <span className="inr">&#8377;</span>;
};

const SubscriptionDetails: React.FC<Props> = ({ subscription }) => {
  const { deeplinkUrl } = useAppSelector((state) => state.app);

  const subscriberCurrency =
    subscription.orders?.[0]?.subscriberCurrency || 'INR';
  const currencyKey = String(subscriberCurrency).toLowerCase();

  const mangoId = subscription.mango?._id;

  const installmentRows: InstallmentRow[] = useMemo(() => {
    const sorted = sortByInstallment(subscription.flexiPayInstallMentDetails);
    const firstUnpaidIndex = sorted.findIndex((inst) => !isPaid(inst));

    return sorted.map((inst, index) => {
      const installmentNo = getInstallmentNumber(inst);
      const displayIndex = installmentNo ?? index + 1;
      const paid = isPaid(inst);
      const isDue = !paid && index === firstUnpaidIndex;

      let status: InstallmentRow['status'] = 'UPCOMING';
      if (paid) status = 'PAID';
      else if (isDue) status = 'DUE';

      const amountVal = getAmountForCurrency(inst, currencyKey);

      const dueRaw =
        inst?.calculatedDueDate ||
        inst?.dueDate ||
        inst?.dueAt ||
        inst?.scheduledFor ||
        inst?.scheduledDueDate;
      const subscriptionNext = subscription.flexiPayNextDueDate;

      let dueLabel = '—';
      if (dueRaw != null && moment(dueRaw).isValid()) {
        dueLabel = moment(dueRaw).format('MMM DD, YYYY');
      } else if (
        subscriptionNext != null &&
        moment(subscriptionNext).isValid()
      ) {
        dueLabel = moment(subscriptionNext).format('MMM DD, YYYY');
      }

      const matchedOrder = findOrderForInstallment(
        subscription.orders,
        installmentNo,
        displayIndex,
      );

      return {
        key: `${installmentNo ?? index}-${index}`,
        installmentNo,
        displayIndex,
        amountVal,
        dueLabel,
        status,
        isDue,
        paid,
        invoiceUrl: matchedOrder?.invoice,
      };
    });
  }, [
    subscription.flexiPayInstallMentDetails,
    subscription.flexiPayNextDueDate,
    subscription.orders,
    currencyKey,
  ]);

  const transactionRows = useMemo(() => {
    const sorted = sortByInstallment(subscription.flexiPayInstallMentDetails);
    const paid = (subscription.flexiPayInstallMentDetails || []).filter(
      (inst) => inst.paidOn && moment(inst.paidOn).isValid(),
    );
    return paid
      .map((inst, i) => {
        const instNo = getInstallmentNumber(inst);
        const sortedIndex = sorted.findIndex((x) => x === inst);
        const fallbackIndex = sortedIndex >= 0 ? sortedIndex + 1 : i + 1;
        const matchedOrder = findOrderForInstallment(
          subscription.orders,
          instNo,
          fallbackIndex,
        );
        return {
          key: inst._id || `${inst.paidOn}-${i}`,
          paidOn: inst.paidOn as string,
          amount: getAmountForCurrency(inst, currencyKey),
          invoiceUrl: matchedOrder?.invoice,
        };
      })
      .sort((a, b) => moment(b.paidOn).valueOf() - moment(a.paidOn).valueOf());
  }, [
    subscription.flexiPayInstallMentDetails,
    subscription.orders,
    currencyKey,
  ]);

  const summaryAmount =
    subscription.orders?.[0]?.amount != null
      ? subscription.orders[0].amount
      : subscription.mango?.price;

  const statusLine = useMemo(() => {
    if (!subscription.isFlexiPay) return null;
    const next = subscription.flexiPayNextDueDate;
    const days =
      next && moment(next).isValid()
        ? Math.max(0, moment(next).diff(moment(), 'days'))
        : 0;
    const cur = subscription.flexiPayCurrentInstallmentNumber;
    const total = subscription.flexiPayTotalInstallments;
    if (cur != null && total != null) {
      return `Installment ${cur} of ${total} due in ${days} days`;
    }
    return null;
  }, [subscription]);

  const handlePayNow = async () => {
    if (!mangoId) {
      message.warning('Unable to open payment. Please try again later.');
      return;
    }
    let path = `${deeplinkUrl}web/checkout/${mangoId}`;
    try {
      const resp = await API.fetchNewToken(undefined, 1800);
      if (resp?.status === 200) {
        const sessionToken = resp.data.result.accessToken;
        const params = new URLSearchParams();
        if (sessionToken) {
          params.set('sessionToken', encodeURIComponent(sessionToken));
        }
        path += params?.toString() ? `?${params.toString()}` : '';
        window.open(path);
      } else {
        showAppError(resp?.data);
      }
    } catch (err) {
      showAppError(err);
    }
  };

  const handleDownloadInvoice = (invoiceUrl?: string) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    message.warning('Invoice is not available.');
  };

  if (!subscription.flexiPayInstallMentDetails?.length) {
    return (
      <div className="subscriptionDetails">
        <Typography.Text type="secondary">
          No installment details available.
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className="subscriptionDetails">
      <section className="subscriptionDetails__summary">
        <Typography.Title level={5} className="subscriptionDetails__title">
          {subscription.mango?.title}
        </Typography.Title>
        <div className="subscriptionDetails__priceRow">
          <span className="subscriptionDetails__priceAmount">
            {currencySymbolNode(subscriberCurrency)}
            {newNumberFormat(summaryAmount)}
          </span>
          <span className="subscriptionDetails__priceType">/ FLEXIPAY</span>
        </div>
        {statusLine ? (
          <>
            <div className="subscriptionDetails__divider" />
            <Typography.Text className="subscriptionDetails__statusLine">
              {statusLine}
            </Typography.Text>
          </>
        ) : null}
      </section>

      <section className="subscriptionDetails__section">
        <Typography.Text className="subscriptionDetails__sectionLabel">
          TRANSACTION HISTORY
        </Typography.Text>
        {transactionRows.length === 0 ? (
          <div className="subscriptionDetails__card subscriptionDetails__card--empty">
            <Typography.Text type="secondary">
              No transactions yet.
            </Typography.Text>
          </div>
        ) : (
          transactionRows.map((row) => (
            <div key={row.key} className="subscriptionDetails__card">
              <div className="subscriptionDetails__txLeft">
                <span className="subscriptionDetails__txIcon" aria-hidden>
                  <IoCheckmarkCircle size={22} />
                </span>
                <Typography.Text className="subscriptionDetails__txDate">
                  {moment(row.paidOn).format('MMM DD, YYYY [AT] hh:mm A')}
                </Typography.Text>
              </div>
              <div className="subscriptionDetails__txRight">
                <Typography.Text
                  strong
                  className="subscriptionDetails__txAmount">
                  {currencySymbolNode(subscriberCurrency)}
                  {row.amount != null ? newNumberFormat(row.amount) : '—'}
                </Typography.Text>
                {row.invoiceUrl ? (
                  <Button
                    htmlType="button"
                    type="text"
                    className="siteBtn noStyle subscriptionDetails__invoiceBtn"
                    aria-label="Download invoice"
                    icon={<MdDownload size={22} />}
                    onClick={() => handleDownloadInvoice(row.invoiceUrl)}
                  />
                ) : null}
              </div>
            </div>
          ))
        )}
      </section>

      <section className="subscriptionDetails__section">
        <Typography.Text className="subscriptionDetails__sectionLabel">
          INSTALLMENT PLAN
        </Typography.Text>
        {installmentRows.map((row) => (
          <div
            key={row.key}
            className={`subscriptionDetails__installment ${
              row.isDue ? 'subscriptionDetails__installment--due' : ''
            }`}>
            <div className="subscriptionDetails__installmentLeft">
              <Typography.Text className="subscriptionDetails__installmentMeta">
                #{row.displayIndex}
              </Typography.Text>
              <Typography.Text
                strong
                className="subscriptionDetails__installmentAmount">
                {currencySymbolNode(subscriberCurrency)}
                {row.amountVal != null ? newNumberFormat(row.amountVal) : '—'}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                className="subscriptionDetails__installmentDue">
                {row.dueLabel}
              </Typography.Text>
            </div>
            <div className="subscriptionDetails__installmentRight">
              {row.status === 'PAID' ? (
                <>
                  <span className="subscriptionDetails__badge subscriptionDetails__badge--paid">
                    PAID
                  </span>
                  {row.invoiceUrl ? (
                    <Button
                      htmlType="button"
                      type="text"
                      className="siteBtn noStyle subscriptionDetails__invoiceBtn"
                      aria-label="Download invoice"
                      icon={<MdDownload size={22} />}
                      onClick={() => handleDownloadInvoice(row.invoiceUrl)}
                    />
                  ) : null}
                </>
              ) : null}
              {row.isDue ? (
                <Button
                  htmlType="button"
                  className="siteBtn sm subscriptionDetails__payBtn"
                  onClick={handlePayNow}>
                  Pay Now
                </Button>
              ) : null}
              {row.status === 'UPCOMING' ? (
                <span className="subscriptionDetails__badge subscriptionDetails__badge--upcoming">
                  UPCOMING
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default SubscriptionDetails;
